import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { UtensilsCrossed } from "lucide-react";
import { api } from "@/lib/api";

function isHeading(line) {
  const letters = line.replace(/[^A-Za-z]/g, "");
  return letters.length >= 2 && line === line.toUpperCase();
}

export default function PackInfo() {
  const { id } = useParams();
  const [addon, setAddon] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    api.get(`/addons/${id}`)
      .then((r) => { setAddon(r.data); setStatus("ok"); })
      .catch(() => setStatus("error"));
  }, [id]);

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-forest/60">Loading…</div>;
  }
  if (status === "error" || !addon?.info_content?.trim()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg text-center px-6" data-testid="pack-info-empty">
        <div>
          <UtensilsCrossed size={40} className="mx-auto text-brand-terracotta mb-4" />
          <p className="font-display text-2xl text-brand-forest">Nothing here yet</p>
          <p className="text-brand-forest/60 mt-2">This list isn't available. Please check with your host.</p>
        </div>
      </div>
    );
  }

  const lines = addon.info_content.split("\n").map((l) => l.trim()).filter((l) => l.length);
  const title = lines[0] || addon.title;
  const rest = lines.slice(1);
  const subtitle = rest.length && !isHeading(rest[0]) ? rest[0] : "";
  const body = subtitle ? rest.slice(1) : rest;

  // group into sections { heading, items: [] }
  const sections = [];
  let current = { heading: "", items: [] };
  body.forEach((line) => {
    if (isHeading(line)) {
      if (current.heading || current.items.length) sections.push(current);
      current = { heading: line, items: [] };
    } else {
      current.items.push(line);
    }
  });
  if (current.heading || current.items.length) sections.push(current);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="pack-info-page">
      <div className="bg-brand-forest text-white">
        <div className="max-w-3xl mx-auto px-6 py-12 text-center">
          <UtensilsCrossed size={36} className="mx-auto text-brand-terracotta mb-4" />
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="pack-info-title">{title}</h1>
          {subtitle && <p className="text-white/70 mt-3 max-w-xl mx-auto">{subtitle}</p>}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {sections.map((sec, si) => (
          <motion.section
            key={si}
            initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.35, delay: Math.min(si * 0.03, 0.2) }}
            data-testid={`pack-section-${si}`}
          >
            {sec.heading && (
              <h2 className="font-display text-xl sm:text-2xl text-brand-terracotta tracking-tight mb-4 flex items-center gap-3">
                <span className="h-px flex-1 bg-brand-terracotta/20 max-w-[24px]" />{sec.heading}
              </h2>
            )}
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {sec.items.map((it, ii) => (
                <li key={ii} className="flex items-start gap-2.5 text-brand-forest">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-terracotta shrink-0" />
                  <span className="leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </motion.section>
        ))}
        <p className="text-center text-xs text-brand-forest/40 pt-6">MyAZbnb.com</p>
      </div>
    </div>
  );
}
